import React, { FC, PropsWithChildren, ReactNode } from 'react';
import classNames from 'classnames';
import View from '../view';
import Loading from '../loading';
import Result from '../result';
import Button from '../button';

interface ILoadGeniusProps {
  /**
   * css class
   */
  className?: string;
  /**
   * 状态
   * @default content
   */
  status?: 'loading' | 'content' | 'error' | 'empty';
  /**
   * 是否全屏居中（仅 status 不为 'content' 时有效）
   * @default false
   */
  fullCenter?: boolean;
  /**
   * 页面错误态展示文案
   */
  errorText?: string;
  /**
   * 页面错误态按钮展示文案
   */
  errorButtonText?: string;
  /**
   * 错误态时点击按钮回调
   */
  onErrorButtonClick?: () => void;
  /**
   * 页面无数据态展示文案
   * @default '暂无数据'
   */
  emptyText?: string;
  /**
   * 自定义 Loading 态
   */
  renderLoading?: () => ReactNode;
  /**
   * 自定义 Empty 态
   */
  renderEmpty?: () => ReactNode;
  /**
   * 自定义 Error 态
   */
  renderError?: () => ReactNode;
}

const LoadGenius: FC<PropsWithChildren<ILoadGeniusProps>> = ({
  children,
  className,
  status = 'content',
  fullCenter = false,
  emptyText = '暂无数据',
  errorText,
  errorButtonText,
  onErrorButtonClick,
  renderLoading,
  renderEmpty,
  renderError,
}) => {
  const loadClass = classNames(
    'ink-load-genius',
    {
      [`ink-load-genius--${status}`]: status,
      'ink-load-genius--full-center': fullCenter && status !== 'content',
    },
    className,
  );

  if (status === 'loading') {
    return renderLoading ? <>{renderLoading()}</> : <Loading className={loadClass} />;
  }

  if (status === 'error') {
    return renderError ? (
      <>{renderError()}</>
    ) : (
      <Result className={loadClass} type="error" desc={errorText}>
        {errorButtonText && (
          <Button round className="ink-load-genius__error-btn" onClick={onErrorButtonClick} type="primary">
            {errorButtonText}
          </Button>
        )}
      </Result>
    );
  }

  if (status === 'empty') {
    return renderEmpty ? <>{renderEmpty()}</> : <Result className={loadClass} type="empty" desc={emptyText} />;
  }

  return <View className={loadClass}>{children}</View>;
};

export default LoadGenius;
